import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Icons } from 'utoniq-ui'

import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { useVerticalDnD } from '@/lib/dnd'

type FavoriteMember = {
  id: string
  name: string
  avatar: string
  fanCount: number
}

type Props = {
  favoriteMembers: FavoriteMember[]
  onMove: (prevIndex: number, nextIndex: number) => void
  onGoToEdit: (favoriteMemberId: string) => void
  onGoToCreate: () => void
  onDelete: (favoriteMemberId: string) => void
  onGoToUsers: (favoriteMemberId: string) => void
  onShowDeleteModal: (member: FavoriteMember) => void
  loading: boolean
  sortingMember: boolean
}

export const FavoriteMemberDragList: Cell<Props> = ({
  favoriteMembers,
  onMove,
  onGoToEdit,
  onGoToCreate,
  onGoToUsers,
  onDelete,
  onShowDeleteModal,
  loading,
  sortingMember,
}) => {
  const { t } = useTranslation('consoleFavoriteMemberSettings')

  if (loading && !sortingMember) {
    return (
      <CellFrame sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton
              variant="rectangular"
              width={36}
              height={17}
              sx={{ borderRadius: '40px' }}
            />
          </Box>

          <Stack spacing={1}>
            {Array.from({ length: 5 }).map((_, index) => (
              <FavoriteMemberSkeleton key={index} />
            ))}
          </Stack>
        </Stack>
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ flex: 1, mr: 2 }}
          >
            {t('maxMembersMessage')}
          </Typography>
          <Box
            sx={{
              width: 36,
              height: 17,
              flexShrink: 0,
              borderRadius: '40px',
              border: '1px solid',
              borderColor: 'grey.100',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 5,
              py: 2,
            }}
          >
            <Typography variant="subtitle2" color="primary">
              {t('memberCountDisplay', { current: favoriteMembers.length })}
            </Typography>
          </Box>
        </Box>

        <Stack
          spacing={1}
          sx={{
            // プラスアイコンと設定ボタンが被らないよう空白を設定
            '&::after': {
              content: '""',
              display: 'block',
              height: 50,
            },
          }}
        >
          {favoriteMembers.map((member, index) => (
            <FavoriteMemberItem
              key={member.id}
              member={member}
              index={index}
              onMove={onMove}
              onGoToEdit={onGoToEdit}
              onGoToUsers={onGoToUsers}
              onDelete={onDelete}
              onShowDeleteModal={onShowDeleteModal}
              sortingMember={sortingMember}
            />
          ))}
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type FavoriteMemberItemProps = {
  member: FavoriteMember
  index: number
  onMove: (prevIndex: number, nextIndex: number) => void
  onGoToEdit: (favoriteMemberId: string) => void
  onGoToUsers: (favoriteMemberId: string) => void
  onDelete: (favoriteMemberId: string) => void
  onShowDeleteModal: (member: FavoriteMember) => void
  sortingMember: boolean
}

const FavoriteMemberItem: Cell<FavoriteMemberItemProps> = ({
  member,
  index,
  onMove,
  onGoToEdit,
  onGoToUsers,
  onDelete,
  onShowDeleteModal,
  sortingMember,
}) => {
  const { t } = useTranslation('consoleFavoriteMemberSettings')
  const { routerPages, router } = useRouter()

  const { ref, drag, opacity } = useVerticalDnD({
    index,
    onMove,
    accept: 'FAVORITE_MEMBER',
    disabled: sortingMember,
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const options = [
    {
      label: t('editMember'),
      onClick() {
        onGoToEdit(member.id)
        handleClose()
      },
    },
    {
      label: t('memberList'),
      onClick() {
        onGoToUsers(member.id)
        handleClose()
      },
    },
    {
      label: t('delete'),
      onClick() {
        onShowDeleteModal(member)
        handleClose()
      },
    },
  ]

  return (
    <>
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          borderRadius: 1,
          opacity,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s ease',
          },
          '&:hover::before': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <IconButton
          size="small"
          ref={drag}
          disableRipple
          disabled={sortingMember}
          sx={{
            mr: 1,
            opacity: sortingMember ? 0.5 : 1,
            cursor: sortingMember ? 'not-allowed' : 'grab',
          }}
        >
          <Icons.Sort />
        </IconButton>

        <Avatar src={member.avatar} sx={{ width: 40, height: 40, mr: 2 }} />

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ wordWrap: 'break-word', wordBreak: 'break-all' }}
          >
            {member.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('memberCount', { count: member.fanCount })}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'grey.50',
            }}
          >
            <IconButton size="small" onClick={handleClick}>
              <Icons.Setting sx={{ color: 'black' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {options.map(({ label, onClick }) => (
          <MenuItem onClick={onClick} key={label}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const FavoriteMemberSkeleton: Cell<{}> = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        borderRadius: 1,
      }}
    >
      <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="30%" height={16} />
      </Box>
      <Skeleton variant="circular" width={32} height={32} />
    </Box>
  )
}
